import { matchAdvances, type BracketMatch, type BracketRound } from './bracketTypes';

/**
 * Wymiary uzyte do rozmieszczenia drabinki. Wszystkie w pikselach logicznych.
 */
export interface BracketLayoutOptions {
  matchWidth: number;
  matchHeight: number;
  /** Odstep pionowy miedzy meczami pierwszej rundy. */
  matchGap: number;
  /** Odstep poziomy miedzy kolumnami rund. */
  columnGap: number;
  /** Wysokosc naglowka kolumny (nazwa rundy + data). */
  headerHeight: number;
}

export const DEFAULT_BRACKET_LAYOUT: BracketLayoutOptions = {
  matchWidth: 236,
  matchHeight: 64,
  matchGap: 12,
  columnGap: 56,
  headerHeight: 48,
};

export interface PositionedMatch {
  match: BracketMatch;
  roundIndex: number;
  matchIndex: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PositionedRound {
  round: BracketRound;
  roundIndex: number;
  x: number;
  matches: PositionedMatch[];
}

export interface BracketConnector {
  id: string;
  /** Atrybut `d` sciezki SVG — wylacznie odcinki poziome i pionowe. */
  d: string;
  /** Czy laczy mecz rozstrzygniety z kolejna runda (linia w kolorze marki). */
  highlighted: boolean;
}

export interface BracketLayout {
  width: number;
  height: number;
  rounds: PositionedRound[];
  connectors: BracketConnector[];
  options: BracketLayoutOptions;
}

/**
 * Wylicza rozmieszczenie drabinki pucharowej.
 *
 * Pozycje liczymy od pierwszej rundy w prawo: mecz kolejnej rundy siada
 * dokladnie na srodku miedzy swoimi dwoma poprzednikami. Dzieki temu uklad
 * dziala dla dowolnej wielkosci turnieju (8, 16, 32, 64, 128) i nie rozjezdza
 * sie przy niepelnych drabinkach z wolnymi losami — nie zakladamy, ze liczba
 * meczow w rundzie jest potega dwojki.
 *
 * WSZYSTKIE wspolrzedne sa zaokraglane do pelnych pikseli. To warunek ostrosci
 * linii: przy grubosci 2 px i calkowitej wspolrzednej obrys pokrywa cale piksele,
 * wiec nie powstaje rozmycie od wygladzania krawedzi.
 */
export function computeBracketLayout(
  rounds: readonly BracketRound[],
  options: Partial<BracketLayoutOptions> = {},
): BracketLayout {
  const layout: BracketLayoutOptions = { ...DEFAULT_BRACKET_LAYOUT, ...options };
  const { matchWidth, matchHeight, matchGap, columnGap, headerHeight } = layout;

  const positionedRounds: PositionedRound[] = [];

  rounds.forEach((round, roundIndex) => {
    const x = Math.round(roundIndex * (matchWidth + columnGap));
    const previous = positionedRounds[roundIndex - 1];

    const matches: PositionedMatch[] = round.matches.map((match, matchIndex) => {
      let y: number;

      if (!previous) {
        // Pierwsza runda — mecze jeden pod drugim.
        y = headerHeight + matchIndex * (matchHeight + matchGap);
      } else {
        const first = previous.matches[matchIndex * 2];
        const second = previous.matches[matchIndex * 2 + 1];

        if (first && second) {
          // Srodek miedzy srodkami obu poprzednikow.
          const centre = (first.y + second.y + matchHeight) / 2;
          y = centre - matchHeight / 2;
        } else if (first) {
          // Nieparzysta liczba poprzednikow — rownamy do jedynego.
          y = first.y;
        } else {
          // Runda szersza niz poprzednia (uklad nietypowy) — uklad zastepczy.
          y = headerHeight + matchIndex * (matchHeight + matchGap);
        }
      }

      return {
        match,
        roundIndex,
        matchIndex,
        x,
        y: Math.round(y),
        width: matchWidth,
        height: matchHeight,
      };
    });

    positionedRounds.push({ round, roundIndex, x, matches });
  });

  const connectors: BracketConnector[] = [];

  for (let roundIndex = 0; roundIndex < positionedRounds.length - 1; roundIndex += 1) {
    const current = positionedRounds[roundIndex];
    const next = positionedRounds[roundIndex + 1];
    if (!current || !next) continue;

    for (const positioned of current.matches) {
      const parent = next.matches[Math.floor(positioned.matchIndex / 2)];
      if (!parent) continue;

      const fromX = Math.round(positioned.x + positioned.width);
      const fromY = Math.round(positioned.y + positioned.height / 2);
      const toX = Math.round(parent.x);
      const toY = Math.round(parent.y + parent.height / 2);
      const midX = Math.round(fromX + (toX - fromX) / 2);

      connectors.push({
        id: `${positioned.match.id}->${parent.match.id}`,
        d: `M ${fromX} ${fromY} H ${midX} V ${toY} H ${toX}`,
        highlighted: matchAdvances(positioned.match),
      });
    }
  }

  const lastRound = positionedRounds[positionedRounds.length - 1];
  const width = lastRound ? lastRound.x + matchWidth : 0;

  const height = positionedRounds.reduce((max, round) => {
    const last = round.matches[round.matches.length - 1];
    return last ? Math.max(max, last.y + last.height) : max;
  }, headerHeight);

  return { width, height, rounds: positionedRounds, connectors, options: layout };
}
